import React, { useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'

import oldStyles from './_old-modal.module.scss'
import styles from './_modal.module.scss'
import {
  Button,
  ConfirmationPopoverContent,
  type ConfirmationPopoverContentProps,
  FormFooter,
  Icon,
  PopoverHeader,
  SideDrawer,
  type SideDrawerProps,
  useIsClient,
  useMediaQuery,
  useToggle,
} from '../../module'
import BackgroundOverlay from '../BackgroundOverlay/BackgroundOverlay'
import ScrollingContainer from '../ScrollingContainer/ScrollingContainer'
import { c } from '../../translations/LibraryTranslationService'

type WithConfirmation = SideDrawerProps & {
  /** Optionally hide the close icon in the header */
  hideCloseIcon?: never
  /** Optionally disable the outside click "close" functionality  */
  disableOutsideClick?: never
  /** The content to display in the confirmation popover */
  confirmation: Omit<ConfirmationPopoverContentProps, 'type'> & {
    close?: () => void
  }
  /** Optionally hide the header. Defaults to false */
  hideHeader?: never
}

type WithoutConfirmation = SideDrawerProps & {
  confirmation?: never
  /** Optionally hide the close icon in the header */
  hideCloseIcon?: boolean
  /** Optionally disable the outside click "close" functionality  */
  disableOutsideClick?: boolean
  /** Optionally hide the header. Defaults to false */
  hideHeader?: boolean
}

export type ModalProps = WithConfirmation | WithoutConfirmation

const ConfirmationModal = ({
  header,
  body,
  cancelCallout,
  confirmCallout,
  confirmButtonText,
  cancelButtonText,
  type = 'black',
  close,
}: Omit<ConfirmationPopoverContentProps, 'type'> & {
  type?: 'red' | 'blue' | 'green' | 'black'
} & {
  close?: () => void
}): React.JSX.Element => {
  let buttonColor: 'primary' | 'primary-blue' = 'primary'
  switch (type) {
    // We want to use the primary blue button for green and blue. Eventually, we will have only one button color for "primary" and clean this up.
    case 'green':
    case 'blue':
      buttonColor = 'primary-blue'
      break
    default:
      break
  }

  return (
    <>
      <div className={styles.modalBody}>
        <div className={styles.modalHeader}>
          <div className={styles.modalHeaderContent}>{header}</div>
        </div>
        <div className={styles.modalContent}>{body}</div>
      </div>
      <div
        className={`${styles.modalFooterContent} ${styles.confirmationFooterContent}`}
      >
        <FormFooter
          {...(cancelCallout || close
            ? {
                cancelButtonProps: {
                  onClick: () => {
                    cancelCallout?.()
                    close?.()
                  },
                  children: cancelButtonText ?? c('cancel'),
                  styleType: 'secondary',
                },
              }
            : {})}
          saveButtonProps={{
            onClick: () => {
              confirmCallout()
              close?.()
            },
            children: confirmButtonText ?? c('confirm'),
            styleType: buttonColor,
            destructive: type === 'red',
          }}
        />
      </div>
    </>
  )
}

const Modal = (props: ModalProps): React.JSX.Element => {
  const {
    children,
    isOpen,
    closeCallout,
    headerContent,
    footerContent,
    logoUrl,
    hideCloseIcon = false,
    disableOutsideClick = false,
    confirmation,
    hideHeader = false,
    noGradient,
    noContentPadding,
    size = 'md',
    qaTestId = 'modal',
  } = props
  const isNewModalUI = useToggle('modal_ui_update')
  const isMobileView = useMediaQuery({ type: 'max', breakpoint: 'md' })
  const isClient = useIsClient()
  const isMobileOpen = isMobileView && isOpen
  const isDesktopOpen = !isMobileView && isOpen
  const [isTransitioning, setIsTransitioning] = useState(false)
  const timeoutRef = useRef<ReturnType<typeof setTimeout>>(undefined)

  const close = () => {
    setIsTransitioning(true)
    timeoutRef.current = setTimeout(() => {
      closeCallout()
      setIsTransitioning(false)
    }, 250)
  }

  const determineClose = () => {
    if (disableOutsideClick || confirmation) {
      return
    }
    close()
  }

  useEffect(() => {
    return () => {
      clearTimeout(timeoutRef.current)
    }
  }, [])

  return isClient ? (
    <>
      <SideDrawer {...props} isOpen={isMobileOpen} />
      {isDesktopOpen &&
        createPortal(
          <div
            className={
              isNewModalUI
                ? styles.modalOuterContainer
                : oldStyles.modalOuterContainer
            }
            qa-test-id={qaTestId}
          >
            <BackgroundOverlay
              close={determineClose}
              isTransitioning={isTransitioning}
              {...(disableOutsideClick || confirmation
                ? { style: { cursor: 'default' } }
                : {})}
            />
            <div
              className={`${isNewModalUI ? styles.modalContainer : oldStyles.modalContainer} animated ${isTransitioning ? 'fadeOutDown' : 'slideInUp'} ${size ? (isNewModalUI ? styles[size] : oldStyles[size]) : ''}`}
            >
              {confirmation ? (
                isNewModalUI ? (
                  <ConfirmationModal {...confirmation} close={close} />
                ) : (
                  <ConfirmationPopoverContent
                    {...confirmation}
                    {...(confirmation.cancelCallout
                      ? {
                          cancelCallout: () => {
                            confirmation.cancelCallout?.()
                            close()
                          },
                        }
                      : {})}
                    confirmCallout={() => {
                      confirmation.confirmCallout()
                      close()
                    }}
                  />
                )
              ) : (
                <>
                  {isNewModalUI ? (
                    <div className={styles.modalBody}>
                      {!hideHeader && (
                        <div className={styles.modalHeader}>
                          <div className={styles.modalHeaderContent}>
                            {headerContent}
                          </div>
                          {!hideCloseIcon && (
                            <Button as='unstyled' onClick={close}>
                              <Icon icon='x' color='dark-purple' />
                            </Button>
                          )}
                        </div>
                      )}
                      <div
                        className={`${styles.modalContent} ${noContentPadding ? styles.noPadding : ''}`}
                      >
                        {typeof children !== 'function' ? children : null}
                      </div>
                    </div>
                  ) : (
                    <>
                      {!hideHeader && (
                        <PopoverHeader
                          {...(logoUrl
                            ? { logoUrl }
                            : { headerText: headerContent ?? '' })}
                          {...(!hideCloseIcon ? { closeCallout: close } : {})}
                        />
                      )}
                      <div
                        className={`${oldStyles.modalContent} ${noContentPadding ? oldStyles.noPadding : ''}`}
                      >
                        <ScrollingContainer
                          height='100%'
                          hideGradients={noGradient}
                        >
                          {typeof children !== 'function' ? children : null}
                        </ScrollingContainer>
                      </div>
                    </>
                  )}
                  {footerContent ? (
                    <div
                      className={
                        isNewModalUI
                          ? styles.modalFooterContent
                          : oldStyles.footerContent
                      }
                    >
                      {typeof footerContent === 'function'
                        ? footerContent({
                            close,
                          })
                        : footerContent}
                    </div>
                  ) : null}
                </>
              )}
            </div>
          </div>,
          document.body,
        )}
    </>
  ) : (
    <></>
  )
}

export default Modal
